import React, { useState, useEffect } from 'react';
import { Loader2, Package, ExternalLink } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';

const InventoryTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tenantId = localStorage.getItem('tenant_id') || 'tenant-123';

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/v1/profitability/inventory', {
        headers: { 'X-Tenant-ID': tenantId }
      });
      setData(response.data || response);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header - NO Add Button, Read-Only notice */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-base font-semibold text-white">Inventory Value Summary</h3>
          <p className="text-xs text-gray-400">
            Aggregated from Product Master • 
            <a 
              href="/main/inventory" 
              className="text-blue-400 ml-1 hover:underline inline-flex items-center gap-0.5"
            >
              Manage in Inventory Hub <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
        <button
          onClick={fetchInventory}
          className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700"
        >
          ↻ Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-2">
        <div className="bg-[#1a2744] p-2.5 rounded-lg border border-gray-700/50">
          <p className="text-[10px] text-gray-400 uppercase">Products</p>
          <p className="text-lg font-bold text-white">{data?.summary?.total_products || 0}</p>
          <p className="text-[10px] text-gray-500">{data?.summary?.total_items || 0} with stock</p>
        </div>
        <div className="bg-[#1a2744] p-2.5 rounded-lg border border-gray-700/50">
          <p className="text-[10px] text-gray-400 uppercase">Total Units</p>
          <p className="text-lg font-bold text-white">{(data?.summary?.total_units || 0).toLocaleString()}</p>
        </div>
        <div className="bg-[#1a2744] p-2.5 rounded-lg border border-gray-700/50">
          <p className="text-[10px] text-gray-400 uppercase">Total Value</p>
          <p className="text-lg font-bold text-emerald-400">{formatCurrency(data?.summary?.total_value)}</p>
        </div>
        <div className="bg-[#1a2744] p-2.5 rounded-lg border border-gray-700/50">
          <p className="text-[10px] text-gray-400 uppercase">Avg Cost/Unit</p>
          <p className="text-lg font-bold text-white">{formatCurrency(data?.summary?.avg_cost_per_unit)}</p>
        </div>
      </div>

      {/* Category Breakdown */}
      {data?.by_category && Object.keys(data.by_category).length > 0 && (
        <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg p-3">
          <h4 className="text-xs font-medium text-white mb-2">Value by Category</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(data.by_category)
              .sort((a, b) => b[1].value - a[1].value)
              .slice(0, 8)
              .map(([category, stats]) => (
                <div key={category} className="bg-[#0f1729] p-2 rounded">
                  <p className="text-[10px] text-gray-400 truncate" title={category}>{category}</p>
                  <p className="text-sm font-medium text-emerald-400">
                    {formatCurrency(stats.value)}
                  </p>
                  <p className="text-[10px] text-gray-500">
                    {stats.items} items • {stats.units.toLocaleString()} units
                  </p>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Top Items Table - READ ONLY */}
      <div className="bg-[#1a2744] border border-gray-700/50 rounded-lg overflow-hidden">
        <div className="px-3 py-2 border-b border-gray-700/50 flex justify-between items-center">
          <h4 className="text-xs font-medium text-white">Top Inventory Items by Value</h4>
          <span className="text-[10px] text-gray-500">Showing {data?.items?.length || 0} items</span>
        </div>
        
        {(!data?.items || data.items.length === 0) ? (
          <div className="text-center py-8 text-gray-400">
            <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No inventory items with stock</p>
            <p className="text-xs mt-1">
              <a href="/main/inventory" className="text-blue-400 hover:underline">
                Add products in Inventory Hub
              </a>
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-[#0f1729]">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase">SKU</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase">Product</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-400 uppercase">Category</th>
                <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-400 uppercase">Qty</th>
                <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-400 uppercase">Cost/Unit</th>
                <th className="px-3 py-2 text-right text-[10px] font-medium text-gray-400 uppercase">Total Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {data.items.map((item, index) => (
                <tr key={item._id || index} className="hover:bg-gray-800/30">
                  <td className="px-3 py-2 text-xs font-mono text-blue-400">{item.sku}</td>
                  <td className="px-3 py-2 text-xs text-white truncate max-w-[200px]" title={item.product_name}>
                    {item.product_name}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">{item.category || '-'}</td>
                  <td className="px-3 py-2 text-xs text-right text-white">{item.quantity.toLocaleString()}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono text-white">{formatCurrency(item.cost_per_unit)}</td>
                  <td className="px-3 py-2 text-xs text-right font-mono text-emerald-400">{formatCurrency(item.total_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {/* Footer note */}
        <div className="px-3 py-2 bg-[#0f1729] text-[10px] text-gray-500 text-center border-t border-gray-700/50">
          Data from Product Master (read-only) • 
          <a href="/main/inventory" className="text-blue-400 hover:underline ml-1">
            Manage inventory in Inventory Hub →
          </a>
        </div>
      </div>
    </div>
  );
};

export default InventoryTab;
